import { useCallback, useEffect, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { usePrevious } from '@ui/src';
import { FeeAmountTypeEnum, FeeItem, FeeItemFeeType, FeeLog } from '../types';
import { OrganizationType, CustomFeeFormValues } from '../../types';
import {
  useCreateCustomFeeMutation,
  useDeleteCustomFeeMutation,
  useLazyGetTargetAccountQuery,
} from '../../../customers/store/services/customers';
import { GeneralInfo } from '../../../individual/types';
import { CorporateGeneralInfo } from '../../../corporate/types';
import { clearFeeFormState, getFee } from '../../store/slices/tariff/form';
import { useAppDispatch } from '../../../../../../../store/use-store';

import ROUTES from '../../../../routes';

const useFeeForm = ({
  customerId,
  feeId,
  type,
  fee,
  info,
}: {
  customerId?: string;
  feeId?: string;
  type: OrganizationType;
  fee?: {
    values?: FeeItem | undefined;
    logs?: FeeLog[];
  };
  info?: Partial<GeneralInfo> | Partial<CorporateGeneralInfo>;
}) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [isModified, setIsModified] = useState(false);
  const [logs, setLogs] = useState<FeeLog[]>([]);
  const [defaultFormValues, setDefaultFormValues] =
    useState<CustomFeeFormValues>();
  const [defaultFee, setDefaultFee] = useState<FeeItem>();
  const [popupVisible, setPopupVisible] = useState(false);
  const [isRestoredToDefault, setIsRestoredToDefault] = useState(false);
  const [regularFee, setRegularFee] = useState(false);
  const [feeTypes, setFeeTypes] = useState<FeeItemFeeType[]>([]);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
    setValue,
    setError,
  } = useForm<CustomFeeFormValues>();

  const fields = watch();
  const prevFeeAmountType = usePrevious(fields.feeAmountType);
  const feeCurrencyId = watch('feeCurrencyId');
  const feeCascadeCurrenciesIds = watch('feeCascadeCurrenciesIds');

  const [
    createCustomFee,
    { isLoading: requestInProgress, isSuccess: onSubmitSuccess },
  ] = useCreateCustomFeeMutation();
  const [getTargetAccount, { isLoading, isFetching }] =
    useLazyGetTargetAccountQuery();
  const [deleteFee, { isSuccess, isLoading: deleteInProgress }] =
    useDeleteCustomFeeMutation();

  const getTargetAccountAction = useCallback(
    async (id: string, body: CustomFeeFormValues) => {
      const response = (await getTargetAccount(id)) as {
        data: { data: { fullNumber: string } };
      };

      if (response.data) {
        reset({
          ...body,
          feeTargetAccountNumber: response.data.data.fullNumber,
        });
      }
    },
    [],
  );

  const onSubmit = handleSubmit((values) => {
    if (!values.feeAmountType) {
      setError('feeAmountType', {
        type: 'custom',
        message: 'Write-off type is required',
      });
    } else if (
      values.feeAmountType &&
      values.feeAmountType === 'FIXED' &&
      !values.feeFixedAmount
    ) {
      setError('feeFixedAmount', {
        type: 'custom',
        message: 'Write-off value is required',
      });
    } else if (
      values.feeAmountType === 'PERCENT' &&
      (!values.feePercentAmount || !values.feePercentMinAmount)
    ) {
      if (!values.feePercentAmount) {
        setError('feePercentAmount', {
          type: 'custom',
          message: 'Write-off value is required',
        });
      }

      if (!values.feePercentMinAmount) {
        setError('feePercentMinAmount', {
          type: 'custom',
          message: 'Minimum amount allowed',
        });
      }
    } else {
      const body: CustomFeeFormValues = {
        feeId: values.feeId,
        name: values.name,
        feeTargetAccountNumber: values.feeTargetAccountNumber,
        feeAmountType: values.feeAmountType,
      };

      if (values.feeAmountType === FeeAmountTypeEnum.FIXED) {
        body.feeFixedAmount = Number(values.feeFixedAmount);
      } else {
        body.feePercentAmount = Number(values.feePercentAmount);

        if (values.feePercentMinAmount) {
          body.feePercentMinAmount = Number(values.feePercentMinAmount);
        }

        if (values.feePercentMaxAmount) {
          body.feePercentMaxAmount = Number(values.feePercentMaxAmount);
        }
      }

      if (values?.feeCurrencyId) {
        body.feeCurrencyId = values?.feeCurrencyId;
      }

      if (values?.feeCascadeCurrenciesIds) {
        body.feeCascadeCurrenciesIds = values?.feeCascadeCurrenciesIds;
      }

      if (isModified) {
        deleteFee({
          id: customerId as string,
          feeId: feeId as string,
        });
      }

      createCustomFee({ body, id: customerId as string });
    }
  });

  const onRestoreDefault = () => {
    setIsRestoredToDefault(true);

    reset(defaultFormValues);
    setIsModified(false);
  };

  const deleteFeeAction = () => {
    deleteFee({
      id: customerId as string,
      feeId: feeId as string,
    });

    setIsRestoredToDefault(false);
  };

  let link = '';

  if (type === OrganizationType.INDIVIDUAL) {
    link = ROUTES.CUSTOMERS_INDIVIDUAL_TARIFFS;
  } else {
    link = ROUTES.CUSTOMERS_CORPORATE_TARIFFS;
  }

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(generatePath(link, { customerId }));
    }, 100);
  }, []);

  useEffect(() => {
    if (
      prevFeeAmountType &&
      fields.feeAmountType !== prevFeeAmountType &&
      !isRestoredToDefault
    ) {
      reset({
        ...fields,
        feePercentAmount: 0,
        feeFixedAmount: 0,
        feePercentMinAmount: 0,
        feePercentMaxAmount: 0,
      });
    }
  }, [fields, prevFeeAmountType, reset, isRestoredToDefault]);

  useEffect(() => {
    if (isSuccess && defaultFee) {
      const body: CustomFeeFormValues = {
        feeId: defaultFee.feeId,
        name: defaultFee.name,
        feePercentAmount: defaultFee.feePercentAmount,
        feeFixedAmount: defaultFee.feeFixedAmount,
        feePercentMinAmount: defaultFee.feePercentMinAmount,
        feePercentMaxAmount: defaultFee.feePercentMaxAmount,
        feeAmountType: defaultFee.feeAmountType,
        feeType: defaultFee.fee.id,
      };

      if (defaultFee?.feeCurrencyId) {
        body.feeCurrencyId = defaultFee?.feeCurrencyId;
      }

      if (defaultFee?.feeCascadeCurrenciesIds) {
        body.feeCascadeCurrenciesIds = defaultFee?.feeCascadeCurrenciesIds;
      }

      getTargetAccountAction(defaultFee.feeTargetAccountNumber, body);

      setIsModified(false);
      setDefaultFormValues(body);

      setTimeout(() => {
        setIsRestoredToDefault(false);
      }, 3000);
    }
  }, [isSuccess, defaultFee]);

  useEffect(() => {
    if (fee) {
      const currentDefaultFee = info?.defaultFees?.find(
        (item) => item.fee.id === fee.values?.fee.id,
      );

      if (currentDefaultFee) {
        setDefaultFee(currentDefaultFee);
      }

      if (fee?.values?.fee?.feeType === 'REGULAR') {
        setRegularFee(true);
      }

      const body: CustomFeeFormValues = {
        feeId: fee.values?.feeId as string,
        name: fee.values?.name as string,
        feePercentAmount: fee.values?.feePercentAmount,
        feeFixedAmount: fee.values?.feeFixedAmount,
        feePercentMinAmount: fee.values?.feePercentMinAmount,
        feePercentMaxAmount: fee.values?.feePercentMaxAmount,
        feeAmountType: fee.values?.feeAmountType as string,
        feeType: fee.values?.fee.id,
      };

      const defaultBody: CustomFeeFormValues = {
        feeId: currentDefaultFee?.feeId as string,
        name: currentDefaultFee?.name as string,
        feePercentAmount: currentDefaultFee?.feePercentAmount,
        feeFixedAmount: currentDefaultFee?.feeFixedAmount,
        feePercentMinAmount: currentDefaultFee?.feePercentMinAmount,
        feePercentMaxAmount: currentDefaultFee?.feePercentMaxAmount,
        feeAmountType: currentDefaultFee?.feeAmountType as string,
        feeType: currentDefaultFee?.fee.id,
      };

      if (fee.values?.isModified) {
        setIsModified(true);
      }

      if (fee?.values?.feeCurrencyId) {
        body.feeCurrencyId = fee?.values?.feeCurrencyId;
        defaultBody.feeCurrencyId = currentDefaultFee?.feeCurrencyId;
      }

      if (fee?.values?.feeCascadeCurrenciesIds) {
        body.feeCascadeCurrenciesIds = fee?.values?.feeCascadeCurrenciesIds;
        defaultBody.feeCascadeCurrenciesIds =
          currentDefaultFee?.feeCascadeCurrenciesIds;
      }

      setDefaultFormValues(defaultBody);

      getTargetAccountAction(
        fee.values?.feeTargetAccountNumber as string,
        body,
      );

      if (fee.logs) {
        setLogs(fee?.logs);
      }
    }
  }, [fee]);

  useEffect(() => {
    if (info?.fees && !fee) {
      const currentFeeItem = info?.fees?.find((item) => item.id === feeId);

      if (currentFeeItem) {
        const currentDefaultFee = info?.defaultFees?.find(
          (item) => item.fee.id === currentFeeItem?.fee.id,
        );

        dispatch(getFee(currentFeeItem));

        if (currentFeeItem.fee.feeType === 'REGULAR') {
          setRegularFee(true);
        }

        const body: CustomFeeFormValues = {
          feeId: currentFeeItem.feeId,
          name: currentFeeItem.name,
          feePercentAmount: currentFeeItem.feePercentAmount,
          feeFixedAmount: currentFeeItem.feeFixedAmount,
          feePercentMinAmount: currentFeeItem.feePercentMinAmount,
          feePercentMaxAmount: currentFeeItem.feePercentMaxAmount,
          feeAmountType: currentFeeItem.feeAmountType,
          feeType: currentFeeItem.fee.id,
        };

        const defaultBody: CustomFeeFormValues = {
          feeId: currentDefaultFee?.feeId as string,
          name: currentDefaultFee?.name as string,
          feePercentAmount: currentDefaultFee?.feePercentAmount,
          feeFixedAmount: currentDefaultFee?.feeFixedAmount,
          feePercentMinAmount: currentDefaultFee?.feePercentMinAmount,
          feePercentMaxAmount: currentDefaultFee?.feePercentMaxAmount,
          feeAmountType: currentDefaultFee?.feeAmountType as string,
          feeType: currentDefaultFee?.fee.id,
        };

        if (currentFeeItem?.feeCurrencyId) {
          body.feeCurrencyId = currentFeeItem.feeCurrencyId;
          defaultBody.feeCurrencyId = currentDefaultFee?.feeCurrencyId;
        }

        if (currentFeeItem?.feeCascadeCurrenciesIds) {
          body.feeCascadeCurrenciesIds = currentFeeItem.feeCascadeCurrenciesIds;
          defaultBody.feeCascadeCurrenciesIds =
            currentDefaultFee?.feeCascadeCurrenciesIds;
        }

        setDefaultFormValues(defaultBody);

        if (currentFeeItem?.isModified) {
          setIsModified(true);
        }

        getTargetAccountAction(currentFeeItem.feeTargetAccountNumber, body);
      }
    }
  }, [fee, info?.fees]);

  useEffect(() => {
    if (fee && fee?.values && !feeTypes?.length && fee?.values?.fee) {
      setFeeTypes([fee.values.fee]);
    }
  }, [fee]);

  useEffect(() => {
    if (onSubmitSuccess) {
      handleClose();
    }
  }, [onSubmitSuccess]);

  useEffect(() => {
    setPanelIsOpen(true);

    return () => {
      dispatch(clearFeeFormState());
    };
  }, [dispatch, feeId, feeTypes]);

  return {
    isLoading: isLoading || isFetching,
    feeTypes,
    control,
    errors,
    setPanelIsOpen,
    logs,
    defaultFormValues,
    fields,
    isModified,
    onRestoreDefault,
    regularFee,
    feeCurrencyId,
    feeCascadeCurrenciesIds,
    setValue,
    panelIsOpen,
    isDirty,
    setPopupVisible,
    handleClose,
    requestInProgress,
    deleteInProgress,
    isRestoredToDefault,
    deleteFeeAction,
    onSubmit,
    reset,
    popupVisible,
  };
};

export default useFeeForm;
